import { getCurrency } from "@/utils/currency";
import type { ChangeEvent } from "react";

interface AmountInputProps {
  value: string;
  onChange: (value: string) => void;
  currencyCode?: string;
  error?: string;
  autoFocus?: boolean;
}

function formatInput(value: string): string {
  const [intPart, decPart] = value.split(".");
  const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted;
}

export default function AmountInput({
  value,
  onChange,
  currencyCode = "MXN",
  error,
  autoFocus,
}: AmountInputProps) {
  const currency = getCurrency(currencyCode);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value.replace(/[^\d.]/g, "");
    const parts = raw.split(".");
    if (parts.length > 2) {
      raw = parts[0] + "." + parts.slice(1).join("");
    }
    const [int, dec] = raw.split(".");
    if (dec !== undefined && dec.length > 2) {
      raw = `${int}.${dec.slice(0, 2)}`;
    }
    onChange(raw);
  };

  return (
    <div>
      <div
        className={`flex items-center rounded-lg border bg-background-50 px-4 py-4 transition-colors focus-within:ring-2 ${
          error
            ? "border-red-400 focus-within:ring-red-400"
            : "border-secondary-300 focus-within:ring-primary-400"
        }`}
      >
        <span className="text-2xl md:text-3xl font-bold text-primary-600 mr-1">
          {currency.symbol}
        </span>
        <input
          type="text"
          inputMode="decimal"
          value={formatInput(value)}
          onChange={handleChange}
          placeholder="0.00"
          autoFocus={autoFocus}
          className="flex-1 min-w-0 bg-transparent text-2xl md:text-3xl font-heading font-bold text-foreground-950 focus:outline-none placeholder:text-foreground-300"
        />
        <span className="ml-2 text-sm font-semibold text-foreground-500 whitespace-nowrap">
          {currency.code}
        </span>
      </div>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}